<template>
  <div class="i-contract-editor" :style="{ '--editor-height': minHeight + 'px' }">
    <div class="contract-editor-sticky">
      <div class="contract-editor-head">
        <el-button
          type="text"
          size="mini"
          :icon="sourceMode ? 'el-icon-edit-outline' : 'el-icon-document'"
          @click="toggleSourceMode"
        >
          {{ sourceMode ? '返回可视化编辑' : 'HTML 源码' }}
        </el-button>
      </div>

      <div v-show="!sourceMode" ref="toolbar" class="contract-editor-toolbar"></div>
    </div>

    <div v-show="!sourceMode" ref="editor" class="contract-editor-body"></div>

    <textarea
      v-show="sourceMode"
      v-model="sourceHtml"
      class="contract-editor-source"
      :placeholder="placeholder"
      @input="onSourceInput"
    />
  </div>
</template>

<script>
import E from 'wangeditor'
import 'wangeditor/release/wangEditor.min.css'
import http from '@/api/http'

export default {
  name: 'i-contract-editor',

  props: {
    value: {
      type: String,
      default: '',
    },
    minHeight: {
      type: Number,
      default: 480,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '请输入合同内容...',
    },
  },

  data() {
    return {
      editor: null,
      innerValue: '',
      isChanging: false,
      sourceMode: false,
      sourceHtml: '',
    }
  },

  methods: {
    initEditor() {
      const editor = new E(this.$refs.toolbar, this.$refs.editor)
      const _self = this

      editor.customConfig.menus = [
        'head',
        'bold',
        'fontSize',
        'fontName',
        'italic',
        'underline',
        'strikeThrough',
        'foreColor',
        'backColor',
        'link',
        'list',
        'justify',
        'quote',
        'emoticon',
        'image',
        'table',
        'code',
        'undo',
        'redo',
        'fullscreen',
      ]

      editor.customConfig.zIndex = 20
      editor.customConfig.pasteFilterStyle = false
      editor.customConfig.pasteIgnoreImg = false
      editor.customConfig.placeholder = this.placeholder

      editor.customConfig.onchange = function (html) {
        if (_self.isChanging || _self.sourceMode) return
        _self.innerValue = html
        _self.$emit('input', html)
      }

      editor.customConfig.customUploadImg = function (files, insert) {
        if (!files || !files.length) return
        const file = files[0]
        const formData = new FormData()
        formData.append('image', file)
        http.upload(formData).then(res => {
          if (res && res.status === 'success' && res.data && res.data.file_url) {
            insert(res.data.file_url)
          }
        })
      }

      editor.create()
      this.editor = editor
      this.applyScrollLayout()

      if (this.readOnly) {
        editor.$textElem.attr('contenteditable', false)
      }

      if (this.value) {
        this.setHtml(this.value, false)
      }
    },

    applyScrollLayout() {
      this.$nextTick(() => {
        if (!this.editor || !this.editor.$textContainerElem) return
        const height = this.minHeight + 'px'
        const { $textContainerElem, $textElem } = this.editor

        $textContainerElem.css({
          height,
          maxHeight: height,
          minHeight: height,
          overflow: 'hidden',
        })

        $textElem.css({
          height: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
        })
      })
    },

    toggleSourceMode() {
      if (!this.sourceMode) {
        this.sourceHtml = this.getHtml()
        this.sourceMode = true
        return
      }

      this.sourceMode = false
      this.setHtml(this.sourceHtml)
    },

    onSourceInput() {
      this.innerValue = this.sourceHtml
      this.$emit('input', this.sourceHtml)
    },

    insertHtml(html) {
      if (this.sourceMode) {
        this.sourceHtml += html
        this.onSourceInput()
        return
      }
      if (!this.editor) return
      this.editor.selection.restoreSelection()
      this.editor.cmd.do('insertHTML', html)
    },

    getHtml() {
      if (this.sourceMode) {
        return this.sourceHtml
      }
      if (!this.editor) return ''
      return this.editor.txt.html()
    },

    setHtml(html, emit = true) {
      const content = html || ''
      this.isChanging = true
      this.innerValue = content
      this.sourceHtml = content

      if (this.editor) {
        this.editor.txt.html(content)
        this.applyScrollLayout()
      }

      if (emit) {
        this.$emit('input', content)
      }

      this.$nextTick(() => {
        this.isChanging = false
      })
    },

    getText() {
      if (!this.editor) return ''
      return this.editor.txt.text()
    },

    clear() {
      this.setHtml('')
    },
  },

  watch: {
    value(newVal) {
      const next = newVal || ''
      if (next === this.innerValue) return
      this.setHtml(next, false)
    },
    minHeight(val) {
      this.applyScrollLayout()
    },
    readOnly(val) {
      if (this.editor && this.editor.$textElem) {
        this.editor.$textElem.attr('contenteditable', val ? false : true)
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initEditor()
    })
  },

  beforeDestroy() {
    if (this.editor && this.editor.$textElem) {
      this.editor.$textElem.off()
    }
    this.editor = null
  },
}
</script>

<style>
.i-contract-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.i-contract-editor .contract-editor-sticky {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
}

.i-contract-editor .contract-editor-head {
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px;
  border-bottom: 1px solid #ebeef5;
  background: #fafafa;
}

.i-contract-editor .contract-editor-toolbar {
  border-bottom: 1px solid #ebeef5;
  background: #fafafa;
}

.i-contract-editor .w-e-toolbar {
  flex-wrap: wrap;
  background: #fafafa !important;
  border: none !important;
}

.i-contract-editor .contract-editor-body.w-e-text-container {
  height: var(--editor-height, 480px) !important;
  max-height: var(--editor-height, 480px) !important;
  min-height: var(--editor-height, 480px) !important;
  border: none !important;
  overflow: hidden !important;
}

.i-contract-editor .contract-editor-body.w-e-text-container .w-e-text {
  box-sizing: border-box;
  height: 100% !important;
  max-height: 100% !important;
  overflow-y: auto !important;
  overflow-x: hidden;
  padding: 16px 20px;
  line-height: 1.8;
  font-size: 14px;
}

.i-contract-editor .w-e-text-container .w-e-text p {
  margin: 0 0 8px;
}

.i-contract-editor .w-e-text-container .w-e-text table {
  border-collapse: collapse;
  width: 100%;
  margin: 10px 0;
}

.i-contract-editor .w-e-text-container .w-e-text table td,
.i-contract-editor .w-e-text-container .w-e-text table th {
  border: 1px solid #dcdfe6;
  padding: 6px 10px;
  min-width: 60px;
  vertical-align: top;
}

.i-contract-editor .contract-editor-source {
  display: block;
  width: 100%;
  box-sizing: border-box;
  height: var(--editor-height, 480px);
  border: none;
  border-top: 1px solid #ebeef5;
  outline: none;
  resize: none;
  overflow-y: auto;
  padding: 16px 20px;
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #303133;
  background: #fdfdfd;
}
</style>



// WEBPACK FOOTER //
// src/components/i-contract-editor.vue